import { defModule, Task, type TaskMessage } from '@st/redux'
import type { SDKRequestFor } from '@st/sdk'
import type { FileEntry } from '@st/util/archive/file-entry'
import { Progress } from '@st/util/progress'
import { asc, sort } from '@st/util/sort'
import { create } from 'mutative'
import { Effect, Stream } from 'effect'
import { match, P } from '@st/pattern'
import {
  BrowserResources,
  SdkClient,
  XhrUpload,
  type BrowserResourceCapture,
  type BrowserResourceRef,
  type SdkFailure,
  type XhrUploadError
} from '~/effects'
import { runSdkRequest } from '~/features/module-runtime'
import { observeRoute } from '~/features/module-runtime'

/**
 * Every upload, completion, and Task session belongs to the current folder interval.
 * Moving within that folder retains the whole interval; changing or leaving it clears
 * the maps together, which revokes their Tasks through ordinary reconciliation.
 */
type DocumentUploadsState = {
  activeFolderId: string | null
  nextSequence: number
  uploads: Record<string, Upload>
  completedUploads: Record<string, { documentId: string }>
  sessions: Record<string, UploadSession>
}

type UploadBase = {
  id: string
  progress: Progress
  sequence: number
  status: 'running' | 'failed'

  filesize: number
  filename: string

  folderId: string
  mimeType: string

  documentTypeId?: string | undefined
  questionnaireDocumentTypeId?: string | undefined
  checklistItemId?: string | undefined
  meta: Record<string, string>
}

export type DocumentUploadFailure = 'empty_file' | 'network_failure' | 'rejected'

export type RunningUpload = UploadBase & {
  status: 'running'
}

export type FailedUpload = UploadBase & {
  status: 'failed'
  error: DocumentUploadFailure
}

export type Upload = RunningUpload | FailedUpload

type UploadRequested = {
  type: 'uploadRequested'
  file: {
    resource: BrowserResourceRef<File> | null
    name: string
    size: number
    type: string
  }
  folderId: string
  documentTypeId?: string
  questionnaireDocumentTypeId?: string
  checklistItemId?: string
  // A stable domain key for callers that need to observe one specific upload.
  uploadKey?: string
  meta?: Record<string, string>
}

type SelectedUploadFile = {
  path: string
  name: string
  size: number
  type: string
}

type UploadSelectionRequested = {
  type: 'uploadSelectionRequested'
  filesResource: BrowserResourceRef<FileEntry[]>
  files: SelectedUploadFile[]
  folderId: string
}

type UploadSession =
  | {
      type: 'single'
      id: string
      upload: RunningUpload
      resource: BrowserResourceRef<File>
    }
  | {
      type: 'selection'
      id: string
      uploads: Array<{ file: SelectedUploadFile; upload: RunningUpload }>
      filesResource: BrowserResourceRef<FileEntry[]>
    }

const observeFolderRoute = observeRoute({
  name: P.union(
    'organization_folder',
    'organization_folder_debug',
    'organization_preview_questionnaire',
    'client_folder'
  )
})

const runUploadSession = Task.define(uploadSessionProgram, {
  key: (session: UploadSession) => session.id
})

export type Message =
  | TaskMessage<typeof observeFolderRoute>
  | TaskMessage<typeof runUploadSession>
  | UploadRequested
  | UploadSelectionRequested
  | { type: 'uploadProgressed'; uploadId: string; progress: Progress }
  | { type: 'uploadCompleted'; uploadId: string; documentId: string }
  | { type: 'uploadFailed'; uploadId: string; error: DocumentUploadFailure }
  | { type: 'uploadSessionFinished'; sessionId: string }
  | { type: 'clearUpload'; uploadId: string }

export const documentUploadsModule = defModule<{ state: DocumentUploadsState; message: Message }>({
  init: () => {
    return {
      activeFolderId: null,
      nextSequence: 1,
      uploads: {},
      completedUploads: {},
      sessions: {}
    }
  },
  tasks: (state) => [observeFolderRoute(), ...Object.values(state.sessions).map(runUploadSession)],
  update: (state, message) => {
    switch (message.type) {
      case 'routeChanged': {
        const activeFolderId = message.route?.folderId ?? null
        if (activeFolderId == state.activeFolderId) return state

        return create(state, (s) => {
          s.activeFolderId = activeFolderId
          s.sessions = {}
          s.uploads = {}
          s.completedUploads = {}
        })
      }
      case 'uploadSelectionRequested': {
        const folderId = requireActiveFolder(state, message.folderId)
        const uploads = message.files.map((file, index) => ({
          file,
          upload: makeRunningUpload(folderId, {}, file, state.nextSequence + index)
        }))
        const sessionId = `selection-${state.nextSequence}`
        requireAvailableSession(state, sessionId)
        const nextState = create(state, (s) => {
          s.nextSequence += uploads.length
          for (const { upload } of uploads) {
            s.uploads[upload.id] = upload
          }
          s.sessions[sessionId] = {
            type: 'selection',
            id: sessionId,
            uploads,
            filesResource: message.filesResource
          }
        })

        return nextState
      }
      case 'uploadRequested': {
        const folderId = requireActiveFolder(state, message.folderId)
        const sequence = state.nextSequence
        const upload = makeRunningUpload(folderId, message, message.file, sequence)
        requireAvailableSession(state, upload.id)

        return create(state, (s) => {
          s.nextSequence += 1
          delete s.completedUploads[upload.id]
          if (message.file.size == 0) {
            s.uploads[upload.id] = { ...upload, status: 'failed', error: 'empty_file' }
            return
          }
          if (!message.file.resource) {
            throw new Error(`Document upload ${upload.id} is missing its browser resource`)
          }

          s.uploads[upload.id] = upload
          s.sessions[upload.id] = {
            type: 'single',
            id: upload.id,
            upload,
            resource: message.file.resource
          }
        })
      }
      case 'uploadProgressed': {
        requireRunningUpload(state, message.uploadId, message.type)
        return create(state, (s) => {
          s.uploads[message.uploadId].progress = message.progress
        })
      }
      case 'uploadCompleted': {
        requireRunningUpload(state, message.uploadId, message.type)
        return create(state, (s) => {
          delete s.uploads[message.uploadId]
          s.completedUploads[message.uploadId] = {
            documentId: message.documentId
          }
        })
      }
      case 'uploadFailed': {
        const upload = requireRunningUpload(state, message.uploadId, message.type)
        return create(state, (s) => {
          s.uploads[message.uploadId] = {
            ...upload,
            status: 'failed',
            error: message.error
          }
        })
      }
      case 'uploadSessionFinished': {
        requireActiveSession(state, message.sessionId)
        return create(state, (s) => {
          delete s.sessions[message.sessionId]
        })
      }
      case 'clearUpload':
        return create(state, (s) => {
          if (s.uploads[message.uploadId]?.status == 'running') {
            return
          }

          delete s.uploads[message.uploadId]
          delete s.completedUploads[message.uploadId]
        })
    }
  }
})

function makeRunningUpload(
  folderId: string,
  request: {
    documentTypeId?: string
    questionnaireDocumentTypeId?: string
    checklistItemId?: string
    uploadKey?: string
    meta?: Record<string, string>
  },
  file: { name: string; size: number; type: string },
  sequence: number
): RunningUpload {
  return {
    id: uploadId(request.uploadKey, sequence),
    sequence,
    filename: file.name,
    folderId,
    mimeType: file.type,
    filesize: file.size,
    status: 'running',
    progress: { value: 0, max: file.size },
    documentTypeId: request.documentTypeId,
    questionnaireDocumentTypeId: request.questionnaireDocumentTypeId,
    checklistItemId: request.checklistItemId,
    meta: request.meta ?? {}
  }
}

function uploadSessionProgram(session: UploadSession) {
  const finished = Stream.succeed({
    type: 'uploadSessionFinished',
    sessionId: session.id
  } as const)
  switch (session.type) {
    case 'single':
      return Stream.concat(uploadDocumentProgram(session.upload, session.resource), finished)
    case 'selection':
      return Stream.concat(uploadSelectionProgram(session.filesResource, session.uploads), finished)
  }
}

function uploadSelectionProgram(
  filesResource: BrowserResourceRef<FileEntry[]>,
  uploads: Array<{ file: SelectedUploadFile; upload: RunningUpload }>
) {
  return Stream.unwrap(
    Effect.gen(function* () {
      const browserResources = yield* BrowserResources
      const entries = yield* browserResources.take(filesResource)
      const fileByPath = new Map(entries.map((entry) => [entry.path, entry.file]))
      const resolvedUploads: Array<{ file: File; upload: RunningUpload }> = []
      for (const planned of uploads) {
        const file = fileByPath.get(planned.file.path)
        if (!file || file.size != planned.file.size) {
          return yield* Effect.die(
            new Error(`Resolved upload is missing selected file ${planned.file.path}`)
          )
        }
        resolvedUploads.push({ file, upload: planned.upload })
      }

      return Stream.mergeAll(
        resolvedUploads.map(({ file, upload }) => {
          if (upload.filesize == 0) {
            return Stream.succeed(uploadFailedMessage(upload.id, 'empty_file'))
          }
          return uploadFileProgram(upload, file)
        }),
        { concurrency: 'unbounded' }
      )
    })
  )
}

/**
 * One upload session is one folder-owned Task: progress arrives as ordinary Messages,
 * completion continues to finalization, and any transfer failure prevents the
 * finalization Effect from running. No global listener or callback channel is
 * needed to connect transport progress to this module.
 *
 * Its structure is the protocol:
 *
 *   presign -> progress* -> finalize -> completed
 *
 * `Stream.concat` is the ordering boundary between transfer and finalization;
 * named transport failures become one domain Message, while every other
 * failure propagates to the runtime boundary.
 * Route state keeps the Task alive across every interaction in the same folder;
 * leaving that folder removes the declaration and interrupts the transport.
 */
function uploadDocumentProgram(upload: RunningUpload, resource: BrowserResourceRef<File>) {
  return Stream.unwrap(
    Effect.gen(function* () {
      const browserResources = yield* BrowserResources
      const file = yield* browserResources.take(resource)
      return uploadFileProgram(upload, file)
    })
  )
}

function uploadFileProgram(upload: RunningUpload, file: File) {
  return Stream.unwrap(
    Effect.gen(function* () {
      const sdk = yield* SdkClient
      const xhrUpload = yield* XhrUpload
      const generatePresignedUploadUrlRequest = {
        type: 'folders/generatePresignedDocumentUploadURL',
        filename: upload.filename,
        fileSize: file.size,
        folderId: upload.folderId
      } satisfies SDKRequestFor<'folders/generatePresignedDocumentUploadURL'>

      const presigned = yield* runSdkRequest(sdk.send(generatePresignedUploadUrlRequest))
      if (!presigned.ok) {
        return Stream.succeed(sdkUploadFailedMessage(upload.id, presigned.error))
      }

      const progress = xhrUpload
        .putPresignedUrl({
          url: presigned.response.uploadUrl,
          headers: presigned.response.uploadHeaders,
          file
        })
        .pipe(
          Stream.map(
            (progress) =>
              ({
                type: 'uploadProgressed',
                uploadId: upload.id,
                progress: { value: progress.uploadedBytes, max: progress.totalBytes }
              }) as const
          )
        )

      const finalize = Effect.gen(function* () {
        const finalizeDocumentUploadRequest = {
          type: 'folders/finalizeDocumentUpload',
          documentId: presigned.response.documentId,
          documentTypeId: upload.documentTypeId,
          questionnaireDocumentTypeId: upload.questionnaireDocumentTypeId,
          checklistItemId: upload.checklistItemId,
          folderId: upload.folderId
        } satisfies SDKRequestFor<'folders/finalizeDocumentUpload'>

        const finalized = yield* runSdkRequest(sdk.send(finalizeDocumentUploadRequest))
        if (!finalized.ok) {
          return sdkUploadFailedMessage(upload.id, finalized.error)
        }

        return {
          type: 'uploadCompleted',
          uploadId: upload.id,
          documentId: presigned.response.documentId
        } as const
      })

      return Stream.concat(progress, Stream.fromEffect(finalize)).pipe(
        Stream.catchTags({
          NetworkError: (error) => Stream.succeed(toUploadFailureMessage(upload.id, error)),
          HttpError: (error) => Stream.succeed(toUploadFailureMessage(upload.id, error))
        })
      )
    })
  )
}

function sdkUploadFailedMessage(uploadId: string, error: SdkFailure) {
  return match(error)
    .with({ _tag: 'HttpStatusError' }, () => uploadFailedMessage(uploadId, 'rejected'))
    .with({ _tag: P.union('TransportError', 'TimeoutError') }, () =>
      uploadFailedMessage(uploadId, 'network_failure')
    )
    .exhaustive()
}

export type UploadDocumentArgs = Omit<UploadRequested, 'type' | 'file'> & { file: File }

export type UploadDocumentSelectionArgs = Omit<UploadSelectionRequested, 'type'>

export function uploadDocument(
  capture: BrowserResourceCapture,
  { file, ...args }: UploadDocumentArgs
): Message {
  return {
    type: 'uploadRequested',
    ...args,
    file: {
      resource: file.size == 0 ? null : capture(file),
      name: file.name,
      size: file.size,
      type: file.type
    }
  }
}

export function uploadDocumentSelection(args: UploadDocumentSelectionArgs): Message {
  return {
    type: 'uploadSelectionRequested',
    ...args
  }
}

export function selUploads(uploadsState: DocumentUploadsState) {
  const uploads = Object.values(uploadsState.uploads)
  return sort(uploads, asc('sequence'))
}

export function selFailedUploads(uploadsState: DocumentUploadsState) {
  const uploads = Object.values(uploadsState.uploads)
  return sort(uploads, asc('sequence')).filter(isFailedUpload)
}

export function selHasActiveUploads(uploadsState: DocumentUploadsState, folderId?: string) {
  return Object.values(uploadsState.uploads).some((upload) => {
    if (folderId && upload.folderId != folderId) {
      return false
    }
    return upload.status == 'running'
  })
}

export function selQuestionnaireUploadsOfDocumentType(
  uploadsState: DocumentUploadsState,
  documentTypeId: string
) {
  const uploads = Object.values(uploadsState.uploads)
  return sort(uploads, asc('sequence')).filter(
    (upload) => upload.questionnaireDocumentTypeId == documentTypeId
  )
}

export function selUploadForKey(uploadsState: DocumentUploadsState, key: string) {
  return uploadsState.uploads[keyedUploadId(key)]
}

export function selCompletedDocumentForKey(uploadsState: DocumentUploadsState, key: string) {
  return uploadsState.completedUploads[keyedUploadId(key)]?.documentId
}

function uploadId(key: string | undefined, sequence: number) {
  if (key) return keyedUploadId(key)
  return `upload-${sequence}`
}

function keyedUploadId(key: string) {
  return `key:${key}`
}

function isFailedUpload(upload: Upload): upload is FailedUpload {
  return upload.status == 'failed'
}

function uploadFailedMessage(uploadId: string, error: DocumentUploadFailure) {
  return { type: 'uploadFailed', uploadId, error } as const
}

function toUploadFailureMessage(uploadId: string, error: XhrUploadError) {
  switch (error._tag) {
    case 'NetworkError':
      return uploadFailedMessage(uploadId, 'network_failure')
    case 'HttpError':
      return uploadFailedMessage(uploadId, 'rejected')
  }
}

function requireActiveFolder(state: DocumentUploadsState, requestedFolderId: string) {
  if (state.activeFolderId != requestedFolderId) {
    throw new Error(
      `Cannot start document upload for inactive folder ${requestedFolderId}; active folder is ${
        state.activeFolderId ?? 'none'
      }`
    )
  }
  return state.activeFolderId
}

function requireAvailableSession(state: DocumentUploadsState, sessionId: string) {
  if (state.sessions[sessionId]) {
    throw new Error(`Document upload session ${sessionId} is already active`)
  }
}

/** Revoked Task output is suppressed by the runtime before dispatch. Missing ownership here is a bug. */
function requireRunningUpload(
  state: DocumentUploadsState,
  uploadId: string,
  outcome: 'uploadProgressed' | 'uploadCompleted' | 'uploadFailed'
): RunningUpload {
  const upload = state.uploads[uploadId]
  if (!upload || upload.status != 'running') {
    throw new Error(`Unexpected ${outcome} for inactive document upload ${uploadId}`)
  }
  return upload
}

function requireActiveSession(state: DocumentUploadsState, sessionId: string) {
  const session = state.sessions[sessionId]
  if (!session) {
    throw new Error(
      `Unexpected uploadSessionFinished for inactive document upload session ${sessionId}`
    )
  }
  return session
}
